<template>
	<AsiCard unwrapped no-bottom-margin class="flex-grow-1 d-flex flex-column overflow-hidden">
		<div class="content-container" :class="{'is-mobile': sMobile, 'has-image': hasImage | categoryAvatarImage !== null}">
			<template v-if="hasImage">
				<div class="gallery-wrapper">
					<GalleryDetail :image="selectedImage" :aspect-ratio="sMobile ? 2 : 1" :contain="$isMkShop" class="fill-height"/>
					<div v-if="item !== null && item.galleryAttachments.length > 0" class="gallery-preview d-flex flex-row justify-center px-3 py-1">
						<GalleryPreview v-model="selectedImageId" :images="item.galleryAttachments" :small="!sDesktop" dark/>
					</div>
				</div>
				<v-divider class="separator1" :vertical="!sMobile"/>
			</template>
			<template v-else-if="categoryAvatarImage !== null">
				<div class="gallery-wrapper">
					<GalleryDetail :image="categoryAvatarImage" :aspect-ratio="sMobile ? 2 : 1" class="fill-height"/>
				</div>
				<v-divider class="separator1" :vertical="!sMobile"/>
			</template>
			<div class="title-wrapper pa-3">
				<h1>{{ nameTranslated }}</h1>
				<div v-if="item !== null && (item.itemNumber !== null || item.alternativeItemNumber !== null)" class="d-flex flex-row flex-wrap align-center ma-n2">
					<ItemChipNumber v-if="item.itemNumber !== null" :item="item" class="ma-2 px-2" small/>
					<ItemChipAltNumber v-if="item.alternativeItemNumber !== null" :item="item" class="ma-2 px-2" small/>
					<ItemChipCustomerItemNumber v-if="item.customerItem !== null" :customerItem="item.customerItem" class="px-2" small/>
				</div>
			</div>
			<v-divider v-if="!sMobile" class="vertical-separator1"/>
			<div class="content-wrapper pa-3 d-flex flex-column justify-center align-center grey lighten-5">
				<v-icon size="48" class="mb-1">{{ icons.squareDashed }}</v-icon>
				<p class="title mb-0">{{ $t('item.terms.itemHasBeenReplaced') }}</p>
				<p class="subtitle-1">{{ $t('item.terms.itemHasDifferentNumber') }}</p>

			</div>
			<v-divider class="vertical-separator2"/>
			<div class="footer-wrapper pa-3 grey lighten-4 d-flex align-center" :class="{'justify-end': !sMobile}">
					<AsiBtn :disabled="!hasPrices" dense :icon="icons.share" color="primary" @click="handleClick()" large class="add-btn">
						{{ $t('item.terms.goToItem') }}
					</AsiBtn>
			</div>
		</div>

		<v-progress-linear absolute top :indeterminate="loading" :value="100" class="rounded-0"/>

	</AsiCard>
</template>

<script lang="ts">
	import {Component, Prop, Watch} from "vue-property-decorator";
	import {IItemShop} from "@/models/item/ItemShopModels";
	import ItemPriceTable from "@/components/item/ItemPriceTable.vue";
	import AsiCard from "@/components/common/AsiCard.vue";
	import TranslatedValueHelper from "@/models/translated-value/TranslatedValueHelper";
	import AsiBtn from "@/components/common/AsiBtn.vue";
	import {mixins} from "vue-class-component";
	import ResponsiveChecks from "@/mixins/ResponsiveChecks.vue";
	import Icon from "@/plugins/icons";
	import CartSelectInput from "@/components/cart/shop/CartSelectInput.vue";
	import GalleryDetail from "@/components/gallery/GalleryDetail.vue";
	import {IAttachment} from "@/models/attachment/AttachmentModels";
	import GalleryPreview from "@/components/gallery/GalleryPreview.vue";
	import ItemQuantitySelectionWithPrice from "@/components/item/shop/ItemQuantitySelectionWithPrice.vue";
	import ItemAlertStockStatus from "@/components/item/ItemAlertStockStatus.vue";
	import ItemChipNumber from "@/components/item/ItemChipNumber.vue";
	import ItemChipAltNumber from "@/components/item/ItemChipAltNumber.vue";
	import ShoppingListButton from "@/components/shopping-list/shop/ShoppingListButton.vue";
	import AsiAlert from "@/components/common/AsiAlert.vue";
	import {IGlobalConfigAdmin} from "@/models/global-config/GlobalConfigAdminModels";
	import ItemChipCustomerItemNumber from "@/components/item/ItemChipCustomerItemNumber.vue";
	import ItemHelper from "@/models/item/ItemHelper";

	@Component({
		components: {
			ItemChipCustomerItemNumber,
			AsiAlert,
			ShoppingListButton,
			ItemChipAltNumber,
			ItemChipNumber,
			ItemAlertStockStatus,
			ItemQuantitySelectionWithPrice,
			GalleryPreview,
			GalleryDetail,
			CartSelectInput,
			AsiBtn, AsiCard, ItemPriceTable
		}
	})
	export default class ItemCardBasicDataReplacedBy extends mixins(ResponsiveChecks) {

		@Prop({type: Object, default: null})
		public item!: IItemShop | null;

		@Prop({type: Boolean, default: false})
		public loading!: boolean;

		private icons = Icon;

		private selectedCartId: string | null = null;
		private selectedImageId: string | null = null;
		private currentQuantity: number = 0;

		public created(): void {
			this.selectedCartId = this.$store.state.cart.activeCartId;
		}

		private get hasPrices(): boolean {
			return (this.item?.prices.length ?? 0) > 0;
		}

		private get nameTranslated(): string | null {
			return this.item === null ? null : TranslatedValueHelper.get(this.item.name, this.$i18n.locale);
		}

		private get selectedImage(): IAttachment | null {
			return this.item?.galleryAttachments.find(i => i.id === this.selectedImageId) ?? null;
		}

		private get hasImage(): boolean {
			return (this.item?.galleryAttachments.length ?? 0) > 0;
		}

		private get categoryAvatarImage(): IAttachment | null {
			return this.item === null || this.item.category.avatarAttachmentId === null
				? null
				: this.item.category.galleryAttachments.find(i => i.id === this.item?.category.avatarAttachmentId) ?? null;
		}

		private get globalConfig(): IGlobalConfigAdmin | null {
			return this.$store.state.globalConfig.modelAdmin;
		}

		@Watch('item', {deep: true})
		private onItemChanged(): void {
			this.selectedImageId = this.item?.avatarAttachmentId ?? null;
		}


		private handleClick(){
			if (this.item == null || this.item.replacedBy == null) return;

			const route = ItemHelper.detailRoute(this.item.replacedBy.itemNumber);

			if (this.$router.currentRoute.params.id === this.item.replacedBy.id) return;
			if (route !== null) {
				this.$router.push(route);
			}
		}
	}
</script>

<style lang="scss" scoped>
	@import '../../../../node_modules/vuetify/src/styles/styles';

	.content-container {
		display: grid;

		&:not(.is-mobile) {
			&.has-image {
				grid-template:
					"glry sep1 titl titl" auto
					"glry sep1 vsp1 vsp1" auto
					"glry sep1 content sep2" 1fr
					"glry sep1 vsp2 vsp2" auto
					"glry sep1 foot foot" auto / 25% auto 1fr auto;
			}

			&:not(.has-image) {
				grid-template:
					"titl titl" auto
					"vsp1 vsp1" auto
					"content sep2" 1fr
					"vsp2 vsp2" auto
					"foot foot" auto / 1fr;
			}
		}

		&.is-mobile {
			&.has-image {
				grid-template:
					"glry" auto
					"titl" auto
					"content" auto
					"foot" auto
					"pric" 1fr;
			}

			&:not(.has-image) {
				grid-template:
					"titl" auto
					"content" auto
					"foot" auto
					"pric" 1fr / 1fr;
			}
		}

		.separator1 {
			grid-area: sep1;
			z-index: 1;
		}

		.separator2 {
			grid-area: sep2;
		}

		.title-wrapper {
			grid-area: titl;
		}

		.content-wrapper {
			grid-area: content;
		}

		.gallery-wrapper {
			grid-area: glry;
			position: relative;

			.gallery-preview {
				background-color: rgba(0, 0, 0, 0.5);
				position: absolute;
				bottom: 0;
				width: 100%;
			}
		}

		.add-btn {
			grid-area: addC;
		}


		.vertical-separator1 {
			grid-area: vsp1;
		}

		.vertical-separator2 {
			grid-area: vsp2;
		}

		&:not(.is-mobile) {
			.footer-wrapper {
				grid-area: foot;
			}
		}

		&.is-mobile {
			.footer-wrapper {
				grid-area: foot;
			}

		}
	}
</style>
